import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { API, BACKEND_URL } from "../../App";
import {
  Gift, Copy, ExternalLink, QrCode, Download, Eye,
  Search, Edit3, Upload, X, Save, Image, Video,
  ChevronDown, Link2, Globe, FileVideo
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const DEST_OPTIONS = [
  { value: "landing_page", label: "Landing Page", icon: Globe },
  { value: "tree_page", label: "Wedding Tree", icon: Globe },
  { value: "gratitude_page", label: "Gratitude Page", icon: Globe },
  { value: "sde_page", label: "SDE Page", icon: FileVideo },
  { value: "uploaded_media", label: "Uploaded Media", icon: Image },
  { value: "custom_url", label: "Custom URL", icon: Link2 },
];

export default function CoupleArtifacts() {
  const { wedding } = useOutletContext();
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [qrArtifact, setQrArtifact] = useState(null);
  const [editArtifact, setEditArtifact] = useState(null);

  useEffect(() => { fetchArtifacts(); }, [wedding]);

  const fetchArtifacts = async () => {
    if (!wedding?.wedding_id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/artifacts?wedding_id=${wedding.wedding_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArtifacts(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getTokenUrl = (token) => `${window.location.origin}/t/${token}`;

  const filtered = artifacts.filter(a =>
    a.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.token.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.artifact_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const batches = {};
  const standalone = [];
  filtered.forEach(a => {
    if (a.batch_id) {
      (batches[a.batch_id] = batches[a.batch_id] || []).push(a);
    } else {
      standalone.push(a);
    }
  });

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-wedding-ivory/60">No wedding assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="couple-artifacts">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Wedding Artifacts</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage giveaways, destinations & media</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-wedding-ivory/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search artifacts..."
            className="pl-10 bg-wedding-charcoal border-white/10 w-64"
            data-testid="artifact-search"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Artifacts", value: artifacts.length },
          { label: "Active", value: artifacts.filter(a => a.status === "active").length },
          { label: "Batches", value: Object.keys(batches).length },
          { label: "With Media", value: artifacts.filter(a => a.uploaded_media_url).length },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Gift className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
          <p className="text-wedding-ivory/60">No artifacts assigned to your wedding yet</p>
          <p className="text-wedding-ivory/40 text-sm mt-2">Contact your admin to set up giveaways</p>
        </div>
      ) : (
        <div className="space-y-8">
          {standalone.length > 0 && (
            <div>
              <h2 className="font-serif text-xl text-wedding-ivory mb-4">Individual Artifacts</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {standalone.map((a, i) => (
                  <ArtifactCard
                    key={a.artifact_id}
                    artifact={a}
                    index={i}
                    onShowQR={() => setQrArtifact(a)}
                    onEdit={() => setEditArtifact(a)}
                    onCopy={copyToClipboard}
                    getTokenUrl={getTokenUrl}
                  />
                ))}
              </div>
            </div>
          )}
          {Object.entries(batches).map(([batchId, items]) => (
            <div key={batchId}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl text-wedding-ivory">
                  Batch: {items[0]?.label?.split("#")[0]?.trim() || batchId}
                </h2>
                <span className="text-wedding-ivory/50 text-sm">{items.length} items</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((a, i) => (
                  <ArtifactCard
                    key={a.artifact_id}
                    artifact={a}
                    index={i}
                    onShowQR={() => setQrArtifact(a)}
                    onEdit={() => setEditArtifact(a)}
                    onCopy={copyToClipboard}
                    getTokenUrl={getTokenUrl}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Modal */}
      <Dialog open={!!qrArtifact} onOpenChange={() => setQrArtifact(null)}>
        <DialogContent className="bg-wedding-charcoal border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">QR Code</DialogTitle>
          </DialogHeader>
          {qrArtifact && (
            <div className="text-center">
              <div className="qr-code-container mx-auto rounded-sm">
                <QRCodeSVG value={getTokenUrl(qrArtifact.token)} size={200} level="H" />
              </div>
              <p className="text-wedding-ivory/80 mt-4 font-mono text-sm">{qrArtifact.token}</p>
              <p className="text-wedding-ivory/50 text-xs mt-2">{qrArtifact.label}</p>
              <Button
                onClick={() => {
                  const svg = document.querySelector(".qr-code-container svg");
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");
                  const img = new window.Image();
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0);
                    const a = document.createElement("a");
                    a.download = `qr-${qrArtifact.token}.png`;
                    a.href = canvas.toDataURL("image/png");
                    a.click();
                  };
                  img.src = "data:image/svg+xml;base64," + btoa(svgData);
                }}
                className="btn-secondary mt-4"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4 mr-2" />Download
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      {editArtifact && (
        <EditArtifactModal
          artifact={editArtifact}
          weddingSlug={wedding.slug}
          onClose={() => setEditArtifact(null)}
          onSaved={() => { setEditArtifact(null); fetchArtifacts(); }}
        />
      )}
    </div>
  );
}

/* ── Artifact Card ────────────────────────────── */
function ArtifactCard({ artifact, index, onShowQR, onEdit, onCopy, getTokenUrl }) {
  const getMediaUrl = (url) => url?.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const destLabel = DEST_OPTIONS.find(d => d.value === artifact.destination_type)?.label || artifact.destination_type;

  return (
    <motion.div
      className="card-hover p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`artifact-card-${artifact.artifact_id}`}
    >
      {/* Thumbnail preview */}
      {artifact.uploaded_media_url && (
        <div className="mb-4 aspect-video bg-wedding-subtle rounded-sm overflow-hidden">
          {artifact.uploaded_media_url.match(/\.(mp4|mov|webm)$/i) ? (
            <video src={getMediaUrl(artifact.uploaded_media_url)} className="w-full h-full object-cover" muted />
          ) : (
            <img src={getMediaUrl(artifact.uploaded_media_url)} alt="" className="w-full h-full object-cover" />
          )}
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-wedding-ivory font-medium">{artifact.label || artifact.token}</h3>
          <p className="text-wedding-ivory/50 text-sm capitalize">{artifact.artifact_type.replace("_", " ")}</p>
        </div>
        <span className={`badge ${artifact.status === "active" ? "badge-active" : "badge-inactive"}`}>
          {artifact.status}
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-wedding-ivory/40 text-xs uppercase mb-1">Token</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-wedding-subtle px-3 py-2 rounded text-wedding-gold text-sm font-mono truncate">
              {artifact.token}
            </code>
            <button onClick={() => onCopy(artifact.token)} className="p-2 text-wedding-ivory/50 hover:text-wedding-gold transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div>
          <p className="text-wedding-ivory/40 text-xs uppercase mb-1">Destination</p>
          <p className="text-wedding-ivory/70 text-sm">{destLabel}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
        <Button size="sm" onClick={onEdit}
          className="flex-1 bg-wedding-gold/10 text-wedding-gold hover:bg-wedding-gold/20 border border-wedding-gold/30"
          data-testid={`edit-artifact-${artifact.artifact_id}`}
        >
          <Edit3 className="w-4 h-4 mr-1" />Edit
        </Button>
        <Button size="sm" onClick={onShowQR}
          className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10"
          data-testid={`qr-artifact-${artifact.artifact_id}`}
        >
          <QrCode className="w-4 h-4" />
        </Button>
        <Button size="sm" onClick={() => onCopy(getTokenUrl(artifact.token))}
          className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10"
        >
          <Copy className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
}

/* ── Edit Artifact Modal ──────────────────────── */
function EditArtifactModal({ artifact, weddingSlug, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: artifact.label || "",
    destination_type: artifact.destination_type || "landing_page",
    destination_url: artifact.destination_url || "",
    uploaded_media_url: artifact.uploaded_media_url || "",
    status: artifact.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const getMediaUrl = (url) => url?.startsWith("http") ? url : `${BACKEND_URL}${url}`;

  // Auto-generate destination_url for known page types
  const resolveDestUrl = (destType) => {
    if (!weddingSlug) return "";
    switch (destType) {
      case "landing_page": return `/${weddingSlug}`;
      case "tree_page": return `/${weddingSlug}/tree`;
      case "gratitude_page": return `/${weddingSlug}/gratitude`;
      case "sde_page": return `/${weddingSlug}/sde/groom`;
      default: return form.destination_url;
    }
  };

  const handleDestTypeChange = (val) => {
    const url = val === "custom_url" || val === "uploaded_media"
      ? form.destination_url
      : resolveDestUrl(val);
    setForm({ ...form, destination_type: val, destination_url: url });
  };

const handleUpload = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const maxSize =
    file.type.startsWith("video/")
      ? 50 * 1024 * 1024
      : file.type.startsWith("audio/")
      ? 20 * 1024 * 1024
      : 10 * 1024 * 1024;

  if (file.size > maxSize) {
    toast.error(
      `File too large. Max ${
        file.type.startsWith("video/")
          ? "50MB"
          : file.type.startsWith("audio/")
          ? "20MB"
          : "10MB"
      }`
    );
    return;
  }

  setUploading(true);
  const fd = new FormData();
  fd.append("file", file);

  try {
    const token = localStorage.getItem("token");

    const mediaType = file.type.startsWith("audio/")
      ? "audio"
      : file.type.startsWith("video/")
      ? "video"
      : "image";

    fd.append("wedding_id", artifact.wedding_id);
    fd.append("media_type", mediaType);

    const res = await axios.post(
      `${API}/media/upload`,
      fd,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    const mediaUrl = res.data.url;

    setForm((prev) => ({
      ...prev,
      uploaded_media_url: mediaUrl,
      destination_type: "uploaded_media",
    }));

    toast.success("Media uploaded!");
  } catch (err) {
    console.error(err);
    toast.error("Upload failed");
  } finally {
    setUploading(false);
  }
};

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/artifacts/${artifact.artifact_id}`,
        {
          label: form.label || null,
          destination_type: form.destination_type,
          destination_url: form.destination_type === "uploaded_media" ? null : form.destination_url || null,
          uploaded_media_url: form.uploaded_media_url || null,
          status: form.status,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Artifact saved!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-wedding-charcoal border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-wedding-ivory">
            Edit Artifact — {artifact.token}
          </DialogTitle>
        </DialogHeader>

        <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime" onChange={handleUpload} className="hidden" />

        <div className="space-y-5 mt-2">
          {/* Label */}
          <div>
            <Label className="form-label">Label</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Table 5 Orb"
              className="input-premium mt-2"
              data-testid="artifact-label-input"
            />
          </div>

          {/* Destination Type */}
          <div>
            <Label className="form-label">Destination Type</Label>
            <Select value={form.destination_type} onValueChange={handleDestTypeChange}>
              <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2" data-testid="dest-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wedding-charcoal border-white/10">
                {DEST_OPTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom URL */}
          {form.destination_type === "custom_url" && (
            <div>
              <Label className="form-label">Custom URL</Label>
              <Input
                value={form.destination_url}
                onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
                placeholder="https://..."
                className="input-premium mt-2"
                data-testid="custom-url-input"
              />
            </div>
          )}

          {/* Auto-resolved URL preview for page types */}
          {["landing_page", "tree_page", "gratitude_page", "sde_page"].includes(form.destination_type) && (
            <div className="p-3 bg-wedding-subtle rounded-sm">
              <p className="text-wedding-ivory/40 text-xs uppercase mb-1">Redirects to</p>
              <p className="text-wedding-gold text-sm font-mono">{form.destination_url || "(auto)"}</p>
            </div>
          )}

          {/* Upload Media Section */}
          {form.destination_type === "uploaded_media" && (
            <div>
              <Label className="form-label">Upload Media</Label>
              <p className="text-wedding-ivory/40 text-xs mb-3">
                Upload an image or video. Scanning this artifact will show the media directly.
              </p>
              {form.uploaded_media_url ? (
                <div className="relative aspect-video bg-wedding-subtle rounded-sm overflow-hidden">
                  {form.uploaded_media_url.match(/\.(mp4|mov|webm)$/i) ? (
                    <video
                      src={getMediaUrl(form.uploaded_media_url)}
                      className="w-full h-full object-cover"
                      controls
                      data-testid="artifact-media-preview"
                    />
                  ) : (
                    <img
                      src={getMediaUrl(form.uploaded_media_url)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      data-testid="artifact-media-preview"
                    />
                  )}
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="absolute bottom-2 right-2 px-3 py-1.5 bg-wedding-black/80 text-wedding-ivory text-xs rounded hover:bg-wedding-black transition-colors"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full aspect-video border border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-3 rounded-sm"
                  data-testid="artifact-upload-btn"
                >
                  {uploading ? (
                    <div className="w-8 h-8 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-wedding-ivory/40" />
                      <span className="text-wedding-ivory/60 text-sm">Upload Image or Video</span>
                      <span className="text-wedding-ivory/30 text-xs">Images: JPG/PNG/WebP (10MB) | Video: MP4 (50MB)</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <Label className="form-label">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wedding-charcoal border-white/10">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button onClick={onClose} className="btn-secondary" data-testid="cancel-edit-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-artifact-btn">
              {saving ? (
                <div className="w-4 h-4 border-2 border-wedding-black border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
